// # Step 1 Chapter Component
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../auth/state";
import "./step1_chapter.css";
import LibraryHeader from "../lib_components/lib_header";
import WarningFlood from "../lib_components/warning_flood";
import DashboardFooter from "../../../components/DashboardFooter";
import { step1ChpList } from "../chapter_lists/step1ChpList";
// # Import for updating theme preference
import { doc, updateDoc, setDoc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../../../auth/firebase"; // # Import db
import cacheManager from "../../../auth/cache_manager"; // # Import cache manager
import subscriptionManager from "../../../auth/sessions/subscription_session"; // # Import subscription manager

// # Import Material UI components for the slider and checkbox
import { Slider, Checkbox, FormControlLabel } from "@mui/material";

// # Import icons for difficulty levels
import {
  FaRegSmile,
  FaSmile,
  FaMeh,
  FaSadTear,
  FaBook,
  FaHistory,
  FaPlus,
  FaUndo,
  FaTimes,
  FaCheckCircle,
  FaPlayCircle,
} from "react-icons/fa";

// # Import chapter icons
import anatomyIcon from "./step1_icons/anatomy.png";
import biochemistryIcon from "./step1_icons/biochemistry.png";
import bloodIcon from "./step1_icons/blood.png";
import cnsIcon from "./step1_icons/cns.png";
import endocrineIcon from "./step1_icons/endocrine.png";
import eyeIcon from "./step1_icons/eye.png";
import gitIcon from "./step1_icons/git.png";
import heartIcon from "./step1_icons/heart.png";
import immunologyIcon from "./step1_icons/immunology.png";
import infectiousIcon from "./step1_icons/infectious.png";
import kidneyIcon from "./step1_icons/kidney.png";
import lungsIcon from "./step1_icons/lungs.png";
import musculoskeletalIcon from "./step1_icons/musculoskeletal.png";
import pathologyIcon from "./step1_icons/pathology.png";
import pharmaIcon from "./step1_icons/pharma.png";
import psychiatryIcon from "./step1_icons/psychiatry.png";
import publicHealthIcon from "./step1_icons/public_health.png";
import reproductiveIcon from "./step1_icons/reproductive-system.png";
import revisionIcon from "./step1_icons/revision.png";
import skinIcon from "./step1_icons/skin.png";
import statisticsIcon from "./step1_icons/statistics.png";

// # Import Recharts components
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// # Define chapter topics mapping
export const chapterTopics = step1ChpList;

// # Define difficulty options with their properties
const difficultyOptions = [
  {
    value: "n_a",
    label: "N/A",
    icon: FaBook,
    color: "#9E9E9E",
    bgColor: "#F5F5F5",
  },
  {
    value: "easy",
    label: "Easy",
    icon: FaRegSmile,
    color: "#4CAF50",
    bgColor: "#E8F5E9",
  },
  {
    value: "good",
    label: "Good",
    icon: FaSmile,
    color: "#2196F3",
    bgColor: "#E3F2FD",
  },
  {
    value: "hard",
    label: "Hard",
    icon: FaMeh,
    color: "#FF9800",
    bgColor: "#FFF3E0",
  },
  {
    value: "oh_no",
    label: "Oh No",
    icon: FaSadTear,
    color: "#F44336",
    bgColor: "#FFEBEE",
  },
];

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  public_health_sciences: publicHealthIcon,
  biostatistics_and_epidemiology: statisticsIcon,
  ethics_and_communication: publicHealthIcon,
  anatomy_and_histology: anatomyIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_and_oncology: bloodIcon,
  musculoskeletal_and_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon,
  ethics_and_communication_skills: publicHealthIcon,
  quality_healthcare_delivery: publicHealthIcon,
};

// # Display title function - add this function before Step1Chapter
const getDisplayTitle = (chapterId) => {
  // Special cases for better formatting
  const specialCases = {
    ethics_and_communication: "Ethics & Communication",
    hematology_and_oncology: "Hematology & Oncology",
    musculoskeletal_and_connective_tissue:
      "Musculoskeletal & Connective Tissue",
    public_health_sciences: "Public Health Sciences",
    general_pathology: "General Pathology",
    special_senses: "Special Senses",
    rapid_review: "Rapid Review",
  };

  if (specialCases[chapterId]) {
    return specialCases[chapterId];
  }

  // Default formatting for other chapters
  return chapterId
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function Step1Chapter() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [topics, setTopics] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(null);
  const [chartTimeRange, setChartTimeRange] = useState("week");
  // # State for review history modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentTopicHistory, setCurrentTopicHistory] = useState(null);
  // # State for tracking unsaved changes
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  // # State for listener cleanup
  const [unsubscribeListener, setUnsubscribeListener] = useState(null);
  // # State for chapter completion
  const [isChapterComplete, setIsChapterComplete] = useState(false);
  const [chapterCompletedAt, setChapterCompletedAt] = useState(null);
  // # New state for start modal
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isChapterStarted, setIsChapterStarted] = useState(false);
  const [chapterStartedAt, setChapterStartedAt] = useState(null);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (
          userData &&
          userData.themes &&
          userData.themes.lib_theme !== undefined
        ) {
          setIsDarkMode(userData.themes.lib_theme === "dark");
        }
      }
    };

    loadThemePreference();
  }, [user]);

  // # Load chapter completion status
  useEffect(() => {
    const loadChapterStatus = async () => {
      if (!user || !user.uid || !chapterId) return;

      try {
        const libraryProgressRef = doc(db, "my_lib_progress", user.uid);
        const docSnapshot = await getDoc(libraryProgressRef);

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          if (data?.step1 && data.step1[chapterId]) {
            setIsChapterComplete(data.step1[chapterId].iscomplete || false);
            setChapterCompletedAt(data.step1[chapterId].iscomplete_at || null);
          }
        }
      } catch (error) {
        console.error("Error loading chapter status:", error);
        // Initialize with default values on error
        setIsChapterComplete(false);
        setChapterCompletedAt(null);
      }
    };

    loadChapterStatus();
  }, [user, chapterId]);

  // # Load topics data from Firestore
  useEffect(() => {
    if (!user || !user.uid || !chapterId) return;

    // # Setup real-time listener for library progress
    const libraryProgressRef = doc(db, "my_lib_progress", user.uid);

    const unsubscribe = onSnapshot(
      libraryProgressRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const progressData = docSnapshot.data();

          // # Look for step1 -> chapterId -> topics data
          if (progressData?.step1 && progressData.step1[chapterId]) {
            const chapterData = progressData.step1[chapterId];

            // # Set chapter completion status
            setIsChapterComplete(chapterData.iscomplete || false);
            setChapterCompletedAt(chapterData.iscomplete_at || null);

            if (chapterTopics[chapterId]) {
              const updatedTopics = chapterTopics[chapterId].map((topic) => {
                // # If we have stored data for this topic, use it
                if (chapterData[topic.id]) {
                  return {
                    ...topic,
                    ...chapterData[topic.id],
                    IsTopicStarted:
                      chapterData[topic.id].IsTopicStarted || false,
                    TopicStartedAt:
                      chapterData[topic.id].TopicStartedAt || null,
                  };
                }
                // # Otherwise, use default values
                return {
                  ...topic,
                  completed: false,
                  completedAt: null,
                  difficulty: "good",
                  reviewTimestamps: [],
                  reviewProgress: 0,
                  IsTopicStarted: false,
                  TopicStartedAt: null,
                };
              });

              setTopics(updatedTopics);
              setUnsavedChanges(false);
            }
          } else {
            // # Initialize topics with default values if no stored data
            initializeDefaultTopics();
          }
        } else {
          // # Initialize topics with default values if document doesn't exist
          initializeDefaultTopics();
        }
      },
      (error) => {
        console.error("Error setting up library progress listener:", error);
        // # Initialize topics with default values on error
        initializeDefaultTopics();
      },
    );

    // # Store unsubscribe function for cleanup
    setUnsubscribeListener(() => unsubscribe);

    // # Initialize library progress in subscription manager
    subscriptionManager.setupLibraryProgressListener(user.uid);
    subscriptionManager.loadLibraryProgressData(user.uid);

    // # Initialize topics based on chapter if needed
    function initializeDefaultTopics() {
      if (chapterId && chapterTopics[chapterId]) {
        setTopics(
          chapterTopics[chapterId].map((topic) => ({
            ...topic,
            completed: false,
            completedAt: null,
            difficulty: "good", // # Default difficulty is 'good', but will display as N/A until completed
            reviewTimestamps: [],
            reviewProgress: 0,
            IsTopicStarted: false,
            TopicStartedAt: null,
          })),
        );
      }
    }

    // # Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, [user, chapterId]);

  // # Save topics data to Firestore when topics change
  useEffect(() => {
    const saveTopicsToFirestore = async () => {
      if (
        !user ||
        !user.uid ||
        !chapterId ||
        topics.length === 0 ||
        !unsavedChanges
      )
        return;

      try {
        // # Create a data structure to store only the progress data (not the topic content)
        const topicsData = topics.reduce((acc, topic) => {
          acc[topic.id] = {
            completed: topic.completed,
            completedAt: topic.completedAt,
            difficulty: topic.difficulty,
            reviewTimestamps: topic.reviewTimestamps || [],
            reviewProgress: topic.reviewProgress || 0,
          };
          return acc;
        }, {});

        const libraryProgressRef = doc(db, "my_lib_progress", user.uid);

        // # Use a merge update to preserve other chapters' data
        await setDoc(
          libraryProgressRef,
          {
            step1: {
              [chapterId]: topicsData,
            },
          },
          { merge: true },
        );

        setUnsavedChanges(false);
        console.log("Library progress saved successfully");
      } catch (error) {
        console.error("Error saving library progress:", error);
      }
    };

    // # Save after a small delay to batch rapid changes
    const saveTimeout = setTimeout(() => {
      if (unsavedChanges) {
        saveTopicsToFirestore();
      }
    }, 1000);

    return () => clearTimeout(saveTimeout);
  }, [topics, user, chapterId, unsavedChanges]);

  // # Handle theme toggle
  const handleThemeToggle = async (darkMode) => {
    try {
      setIsDarkMode(darkMode);

      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, "users", user.uid), {
          "themes.lib_theme": darkMode ? "dark" : "light",
        });

        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              lib_theme: darkMode ? "dark" : "light",
            },
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }

      // # Dispatch theme change event for footer and other components
      const themeChangeEvent = new CustomEvent("theme_change", {
        detail: { isDark: darkMode },
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error("Error updating theme preference:", error);
    }
  };

  // # Handle completion toggle
  const handleCompletionToggle = (topicId) => {
    setTopics((prevTopics) =>
      prevTopics.map((topic) => {
        if (topic.id === topicId) {
          // # Prevent marking as complete if not started
          if (!topic.IsTopicStarted && !topic.completed) {
            return topic;
          }
          const newCompleted = !topic.completed;
          return {
            ...topic,
            completed: newCompleted,
            completedAt: newCompleted ? new Date().toISOString() : null,
          };
        }
        return topic;
      }),
    );
    setUnsavedChanges(true);
  };

  // # Handle difficulty change
  const handleDifficultyChange = (topicId, newDifficulty) => {
    setTopics((prevTopics) =>
      prevTopics.map((topic) => {
        if (topic.id === topicId) {
          return {
            ...topic,
            difficulty: newDifficulty,
          };
        }
        return topic;
      }),
    );
    setUnsavedChanges(true);
  };

  // # Handle review increment
  const handleReviewIncrement = (topicId, e) => {
    e.stopPropagation();
    setShowConfirmation(topicId);
  };

  // # Handle review confirmation
  const handleReviewConfirm = (topicId, e) => {
    e.stopPropagation();
    setTopics((prevTopics) =>
      prevTopics.map((topic) => {
        if (topic.id === topicId) {
          const currentTime = new Date().toISOString();
          const reviewTimestamps = [
            ...(topic.reviewTimestamps || []),
            currentTime,
          ];
          return {
            ...topic,
            reviewTimestamps,
          };
        }
        return topic;
      }),
    );
    setShowConfirmation(null);
    setUnsavedChanges(true);
  };

  // # Handle review cancel
  const handleReviewCancel = (e) => {
    e.stopPropagation();
    setShowConfirmation(null);
  };

  // # Helper function to get the most recent review timestamp
  const getLastReviewTimestamp = (reviewTimestamps) => {
    if (!reviewTimestamps || reviewTimestamps.length === 0) return null;
    // # Sort timestamps and get the most recent one
    return [...reviewTimestamps].sort().pop();
  };

  // # Helper function to get review count
  const getReviewCount = (reviewTimestamps) => {
    return reviewTimestamps ? reviewTimestamps.length : 0;
  };

  // # Handle history button click
  const handleHistoryClick = (topic, e) => {
    e.stopPropagation();
    setCurrentTopicHistory(topic);
    setShowHistoryModal(true);
  };

  // # Close history modal
  const closeHistoryModal = (e) => {
    if (e) e.stopPropagation();
    setShowHistoryModal(false);
    setCurrentTopicHistory(null);
  };

  // # Calculate detailed statistics
  const calculateStats = () => {
    if (!topics.length) return null;

    const completedCount = topics.filter((t) => t.completed).length;
    const totalReviews = topics.reduce(
      (sum, t) => sum + getReviewCount(t.reviewTimestamps),
      0,
    );
    const maxPossibleReviews = topics.length * 5;
    const reviewProgress = Math.round(
      (totalReviews / maxPossibleReviews) * 100,
    );
    const topicsWithReviews = topics.filter(
      (t) => t.reviewTimestamps?.length > 0,
    ).length;

    // # Calculate difficulty stats with proper initialization
    const difficultyStats = difficultyOptions.reduce((acc, option) => {
      acc[option.value] = 0;
      return acc;
    }, {});

    // Count topics for each difficulty
    topics.forEach((topic) => {
      if (topic.difficulty && topic.completed) {
        difficultyStats[topic.difficulty]++;
      }
    });

    // Convert to percentages
    const difficultyPercentages = difficultyOptions.reduce((acc, option) => {
      // Skip the N/A option for stats
      if (option.value === "n_a") return acc;

      const count = difficultyStats[option.value];
      const totalCompletedTopics =
        topics.filter((t) => t.completed).length || 1; // Avoid division by zero
      acc[option.value] = {
        count,
        percentage: Math.round((count / totalCompletedTopics) * 100),
        label: option.label,
        color: option.color,
      };
      return acc;
    }, {});

    // Only get most reviewed if there are any reviews
    let mostReviewed = null;
    if (totalReviews > 0) {
      const sortedByReviews = [...topics].sort(
        (a, b) =>
          getReviewCount(b.reviewTimestamps) -
          getReviewCount(a.reviewTimestamps),
      );
      mostReviewed =
        getReviewCount(sortedByReviews[0].reviewTimestamps) > 0
          ? sortedByReviews[0]
          : null;
    }

    // # Prepare chart data for different time ranges
    const getDateRange = (days) => {
      return Array.from({ length: days }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split("T")[0];
      }).reverse();
    };

    const getMonthRange = () => {
      const today = new Date();
      const result = [];
      // # Get data for last 30 days instead of actual last month
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        result.push(date.toISOString().split("T")[0]);
      }
      return result;
    };

    const getYearRange = () => {
      // const today = new Date();
      const result = [];
      // # Get data by month for the last 12 months
      for (let i = 12; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        // # Set to first day of month for cleaner data points
        date.setDate(1);
        result.push(date.toISOString().split("T")[0]);
      }
      return result;
    };

    const createChartData = (dates) => {
      // # For weekly view, use exact date matching
      if (dates.length <= 7) {
        return dates.map((date) => ({
          date,
          reviews: topics.reduce((sum, topic) => {
            if (topic.reviewTimestamps) {
              const matchingTimestamps = topic.reviewTimestamps.filter(
                (timestamp) => timestamp.split("T")[0] === date,
              );
              return sum + matchingTimestamps.length;
            }
            return sum;
          }, 0),
          completions: topics.reduce((sum, topic) => {
            if (topic.completedAt && topic.completedAt.split("T")[0] === date) {
              return sum + 1;
            }
            return sum;
          }, 0),
        }));
      }
      // # For monthly view, use daily aggregation
      else if (dates.length <= 31) {
        return dates.map((date) => ({
          date,
          reviews: topics.reduce((sum, topic) => {
            if (topic.reviewTimestamps) {
              const matchingTimestamps = topic.reviewTimestamps.filter(
                (timestamp) => timestamp.split("T")[0] === date,
              );
              return sum + matchingTimestamps.length;
            }
            return sum;
          }, 0),
          completions: topics.reduce((sum, topic) => {
            if (topic.completedAt && topic.completedAt.split("T")[0] === date) {
              return sum + 1;
            }
            return sum;
          }, 0),
        }));
      }
      // # For yearly view, use monthly aggregation
      else {
        return dates.map((monthStart) => {
          const year = parseInt(monthStart.split("-")[0]);
          const month = parseInt(monthStart.split("-")[1]) - 1;
          const nextMonth = new Date(year, month + 1, 1);
          const monthEnd = new Date(nextMonth - 1).toISOString().split("T")[0];

          return {
            date: monthStart,
            reviews: topics.reduce((sum, topic) => {
              if (topic.reviewTimestamps) {
                const monthlyReviews = topic.reviewTimestamps.filter(
                  (timestamp) => {
                    const reviewDate = timestamp.split("T")[0];
                    return reviewDate >= monthStart && reviewDate <= monthEnd;
                  },
                );
                return sum + monthlyReviews.length;
              }
              return sum;
            }, 0),
            completions: topics.reduce((sum, topic) => {
              if (topic.completedAt) {
                const completionDate = topic.completedAt.split("T")[0];
                if (
                  completionDate >= monthStart &&
                  completionDate <= monthEnd
                ) {
                  return sum + 1;
                }
              }
              return sum;
            }, 0),
          };
        });
      }
    };

    return {
      totalTopics: topics.length,
      completedCount,
      completionPercentage: Math.round((completedCount / topics.length) * 100),
      totalReviews,
      topicsWithReviews,
      reviewProgress,
      reviewsPerTopic: (totalReviews / topics.length).toFixed(1),
      mostReviewed: mostReviewed
        ? {
            name: mostReviewed.name,
            count: getReviewCount(mostReviewed.reviewTimestamps),
          }
        : null,
      difficultyStats: difficultyPercentages,
      chartData: {
        week: createChartData(getDateRange(7)),
        month: createChartData(getMonthRange()),
        year: createChartData(getYearRange()),
      },
    };
  };

  const stats = calculateStats();

  // # Load chapter start status
  useEffect(() => {
    const loadChapterStartStatus = async () => {
      if (!user || !user.uid || !chapterId) return;

      try {
        const libraryProgressRef = doc(db, "my_lib_progress", user.uid);
        const docSnapshot = await getDoc(libraryProgressRef);

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          if (data?.step1 && data.step1[chapterId]) {
            setIsChapterStarted(data.step1[chapterId].IsChapStarted || false);
            setChapterStartedAt(data.step1[chapterId].ChptStartedAt || null);
          }
        }
      } catch (error) {
        console.error("Error loading chapter start status:", error);
      }
    };

    loadChapterStartStatus();
  }, [user, chapterId]);

  // # Modified handleTopicClick
  const handleTopicClick = (topic) => {
    // # Check if topic has been started
    if (!topic.IsTopicStarted) {
      setSelectedTopic(topic);
      setShowStartModal(true);
      return;
    }

    // # If already started or after confirmation, navigate to topic
    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
    navigate(`${basePath}/${chapterId}/topic/${topic.id}`);
  };

  // # Handle start confirmation
  const handleStartConfirm = async () => {
    if (!selectedTopic) return;

    const currentTime = new Date().toISOString();
    const updatedTopics = topics.map((topic) => {
      if (topic.id === selectedTopic.id) {
        return {
          ...topic,
          IsTopicStarted: true,
          TopicStartedAt: currentTime,
        };
      }
      return topic;
    });

    setTopics(updatedTopics);

    // # Update Firebase
    try {
      const libraryProgressRef = doc(db, "my_lib_progress", user.uid);

      // # Prepare the update object
      const updateData = {
        step1: {
          [chapterId]: {
            [selectedTopic.id]: {
              ...selectedTopic,
              IsTopicStarted: true,
              TopicStartedAt: currentTime,
            },
          },
        },
      };

      // # If chapter not started, add chapter start data
      if (!isChapterStarted) {
        updateData.step1[chapterId].IsChapStarted = true;
        updateData.step1[chapterId].ChptStartedAt = currentTime;
        setIsChapterStarted(true);
        setChapterStartedAt(currentTime);
      }

      await setDoc(libraryProgressRef, updateData, { merge: true });
      setUnsavedChanges(false);

      // # Navigate to topic
      const isAdmin = window.location.pathname.startsWith("/admin");
      const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
      navigate(`${basePath}/${chapterId}/topic/${selectedTopic.id}`);
    } catch (error) {
      console.error("Error updating start status:", error);
    }

    setShowStartModal(false);
    setSelectedTopic(null);
  };

  // # Handle view only
  const handleViewOnly = () => {
    if (!selectedTopic) return;

    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
    navigate(`${basePath}/${chapterId}/topic/${selectedTopic.id}`);

    setShowStartModal(false);
    setSelectedTopic(null);
  };

  // # Display message if no topics are available
  const NoTopicsMessage = () => (
    <div className="libchp_step1_no-topics">
      <h2>Coming Soon!</h2>
      <p>Topics for this chapter are under development.</p>
    </div>
  );

  // # Effect to check and update chapter completion
  useEffect(() => {
    if (!topics.length) return;

    const allTopicsComplete = topics.every((topic) => topic.completed);

    if (allTopicsComplete !== isChapterComplete) {
      setIsChapterComplete(allTopicsComplete);
      if (allTopicsComplete && !chapterCompletedAt) {
        setChapterCompletedAt(new Date().toISOString());
      } else if (!allTopicsComplete) {
        setChapterCompletedAt(null);
      }
    }
  }, [topics]);

  // # Effect to save chapter completion status
  useEffect(() => {
    const saveChapterStatus = async () => {
      if (!user || !user.uid || !chapterId || !topics.length) return;

      try {
        const libraryProgressRef = doc(db, "my_lib_progress", user.uid);

        // # Create the update object that includes both topics and chapter completion
        const updateData = {
          step1: {
            [chapterId]: {
              // # Topic data
              ...topics.reduce((acc, topic) => {
                acc[topic.id] = {
                  completed: topic.completed,
                  completedAt: topic.completedAt,
                  difficulty: topic.difficulty,
                  reviewTimestamps: topic.reviewTimestamps || [],
                  reviewProgress: topic.reviewProgress || 0,
                  IsTopicStarted: topic.IsTopicStarted || false,
                  TopicStartedAt: topic.TopicStartedAt || null,
                };
                return acc;
              }, {}),
              // # Chapter completion status
              iscomplete: isChapterComplete,
              iscomplete_at: isChapterComplete ? chapterCompletedAt : null,
              // # Chapter start status
              IsChapStarted: isChapterStarted,
              ChptStartedAt: chapterStartedAt,
            },
          },
        };

        await setDoc(libraryProgressRef, updateData, { merge: true });
        setUnsavedChanges(false);
        console.log(
          "Library progress and chapter completion saved successfully",
        );
      } catch (error) {
        console.error("Error saving library progress:", error);
      }
    };

    if (unsavedChanges || isChapterComplete !== null) {
      saveChapterStatus();
    }
  }, [
    topics,
    isChapterComplete,
    chapterCompletedAt,
    user,
    chapterId,
    unsavedChanges,
    isChapterStarted,
    chapterStartedAt,
  ]);

  return (
    <div
      className={`libchp_step1_chapter-container ${
        isDarkMode ? "libchp_step1_dark-mode" : ""
      }`}
    >
      <WarningFlood />
      <LibraryHeader
        isDarkMode={isDarkMode}
        onThemeToggle={handleThemeToggle}
      />

      <main className="libchp_step1_chapter-content">
        {/* Chapter Title Box */}
        <div className="libchp_step1_chapter-header">
          <div className="libchp_step1_chapter-icon-large">
            <img
              src={chapterIcons[chapterId]}
              alt={chapterId}
              className="libchp_step1_chapter-icon-image"
            />
          </div>
          <h1 className="libchp_step1_chapter-name">
            {getDisplayTitle(chapterId)}
          </h1>
          {isChapterComplete && (
            <div
              className="libchp_step1_chapter-complete-icon"
              title="Chapter Completed"
            >
              <FaCheckCircle />
              <span className="libchp_step1_chapter-complete-date">
                {new Date(chapterCompletedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          )}
        </div>

        {/* Chapter Progress Bar */}
        {stats && (
          <div className="libchp_step1_chapter-progress-container">
            <div className="libchp_step1_chapter-progress-stats">
              <span className="libchp_step1_chapter-progress-label">
                Chapter Progress
              </span>
              <span className="libchp_step1_chapter-progress-value">
                {stats.completedCount}/{stats.totalTopics} Topics
              </span>
            </div>
            <div className="libchp_step1_progress-bar">
              <div
                className="libchp_step1_progress-fill"
                style={{ width: `${stats.completionPercentage}%` }}
              ></div>
              <span className="libchp_step1_progress-text">
                {stats.completionPercentage}%
              </span>
            </div>
          </div>
        )}

        {/* Topics Grid */}
        {topics.length > 0 && (
          <div className="libchp_step1_topics-grid">
            {topics.map((topic) => (
              <div key={topic.id} className="libchp_step1_topic-card">
                <div
                  className={`libchp_step1_topic-upper ${
                    showConfirmation === topic.id ? "blurred" : ""
                  }`}
                  onClick={() => handleTopicClick(topic)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="libchp_step1_topic-icon-wrapper">
                    <img
                      src={chapterIcons[chapterId]}
                      alt={topic.name}
                      className="libchp_step1_topic-icon"
                    />
                  </div>
                  <h3 className="libchp_step1_topic-title">{topic.name}</h3>
                </div>

                <div className="libchp_step1_topic-utility">
                  {/* Start Status Row */}
                  <div
                    className={`libchp_step1_start-status ${
                      topic.IsTopicStarted ? "started" : ""
                    }`}
                  >
                    <FaPlayCircle className="libchp_step1_start-icon" />
                    <span>
                      {topic.IsTopicStarted ? "Started" : "Not Started"}
                    </span>
                    {topic.TopicStartedAt && (
                      <span className="libchp_step1_start-date">
                        {new Date(topic.TopicStartedAt).toLocaleString(
                          undefined,
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    )}
                  </div>

                  {/* Status Row with Completion, Date, and Difficulty */}
                  <div className="libchp_step1_status-row">
                    <div className="libchp_step1_completion-status">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={topic.completed}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCompletionToggle(topic.id);
                            }}
                            color="success"
                            size="small"
                            disabled={!topic.IsTopicStarted}
                          />
                        }
                        label={
                          <span
                            style={{
                              color: topic.completed
                                ? "var(--step1-success)"
                                : "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            Complete
                            {topic.completedAt && (
                              <span className="libchp_step1_completion-date">
                                {new Date(topic.completedAt).toLocaleString()}
                              </span>
                            )}
                          </span>
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div
                      className="libchp_step1_difficulty-selector"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={!topic.completed ? "n_a" : topic.difficulty}
                        onChange={(e) =>
                          handleDifficultyChange(topic.id, e.target.value)
                        }
                        style={{
                          backgroundColor: !topic.completed
                            ? difficultyOptions.find(
                                (opt) => opt.value === "n_a",
                              )?.bgColor
                            : difficultyOptions.find(
                                (opt) => opt.value === topic.difficulty,
                              )?.bgColor,
                          color: !topic.completed
                            ? difficultyOptions.find(
                                (opt) => opt.value === "n_a",
                              )?.color
                            : difficultyOptions.find(
                                (opt) => opt.value === topic.difficulty,
                              )?.color,
                          borderColor: !topic.completed
                            ? difficultyOptions.find(
                                (opt) => opt.value === "n_a",
                              )?.color
                            : difficultyOptions.find(
                                (opt) => opt.value === topic.difficulty,
                              )?.color,
                        }}
                        disabled={!topic.completed}
                      >
                        {topic.completed ? (
                          difficultyOptions
                            .filter((option) => option.value !== "n_a")
                            .map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))
                        ) : (
                          <option value="n_a">N/A</option>
                        )}
                      </select>
                      {React.createElement(
                        !topic.completed
                          ? difficultyOptions.find((opt) => opt.value === "n_a")
                              ?.icon
                          : difficultyOptions.find(
                              (opt) => opt.value === topic.difficulty,
                            )?.icon,
                        {
                          className: "libchp_step1_difficulty-icon",
                          style: {
                            color: !topic.completed
                              ? difficultyOptions.find(
                                  (opt) => opt.value === "n_a",
                                )?.color
                              : difficultyOptions.find(
                                  (opt) => opt.value === topic.difficulty,
                                )?.color,
                          },
                        },
                      )}
                    </div>
                  </div>

                  {/* Review Controls Row */}
                  <div className="libchp_step1_review-controls">
                    <button
                      className="libchp_step1_review-button"
                      onClick={(e) => handleReviewIncrement(topic.id, e)}
                      disabled={showConfirmation === topic.id}
                    >
                      <FaPlus />
                      <span>Review</span>
                    </button>

                    <div className="libchp_step1_review-count">
                      {getReviewCount(topic.reviewTimestamps)}
                    </div>

                    <button
                      className="libchp_step1_undo-button"
                      onClick={(e) => handleReviewCancel(e)}
                      disabled={
                        !topic.reviewTimestamps ||
                        topic.reviewTimestamps.length === 0
                      }
                    >
                      <FaUndo />
                    </button>

                    <button
                      className="libchp_step1_history-button"
                      onClick={(e) => handleHistoryClick(topic, e)}
                    >
                      <FaHistory />
                    </button>
                  </div>
                </div>

                {/* Confirmation Dialog */}
                {showConfirmation === topic.id && (
                  <div className="libchp_step1_confirmation-dialog-inline">
                    <span>Confirm review completion?</span>
                    <div className="libchp_step1_confirmation-buttons">
                      <button
                        className="libchp_step1_confirm-yes"
                        onClick={(e) => handleReviewConfirm(topic.id, e)}
                      >
                        Yes
                      </button>
                      <button
                        className="libchp_step1_confirm-no"
                        onClick={handleReviewCancel}
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Chapter Progress - Moved to bottom */}
        {topics.length > 0 && stats && (
          <div className="libchp_step1_stats-card">
            <h2>Chapter Progress</h2>
            <div className="libchp_step1_stats-grid">
              {/* Completion Stats */}
              <div className="libchp_step1_stat-item">
                <span className="libchp_step1_stat-label">Completion</span>
                <span className="libchp_step1_stat-value">
                  {stats.completedCount}/{stats.totalTopics}
                </span>
                <div className="libchp_step1_progress-bar">
                  <div
                    className="libchp_step1_progress-fill"
                    style={{ width: `${stats.completionPercentage}%` }}
                  />
                  <span className="libchp_step1_progress-text">
                    {stats.completionPercentage}%
                  </span>
                </div>
              </div>

              {/* Review Stats */}
              <div className="libchp_step1_stat-item">
                <span className="libchp_step1_stat-label">Reviews</span>
                <div className="libchp_step1_stat-value-group">
                  <span className="libchp_step1_stat-value">
                    {stats.topicsWithReviews}/{stats.totalTopics}
                  </span>
                  <span className="libchp_step1_stat-subtext">
                    topics reviewed
                  </span>
                </div>
                <div className="libchp_step1_progress-bar">
                  <div
                    className="libchp_step1_progress-fill"
                    style={{ width: `${stats.reviewProgress}%` }}
                  />
                  <span className="libchp_step1_progress-text">
                    {stats.totalReviews} total ({stats.reviewProgress}%)
                  </span>
                </div>
              </div>

              {/* Most Reviewed - Only show if there are reviews */}
              {stats.mostReviewed && (
                <div className="libchp_step1_stat-item">
                  <span className="libchp_step1_stat-label">Most Reviewed</span>
                  <span className="libchp_step1_stat-value">
                    {stats.mostReviewed.name}
                  </span>
                  <span className="libchp_step1_stat-percentage">
                    {stats.mostReviewed.count} reviews
                  </span>
                </div>
              )}

              {/* Difficulty Distribution */}
              <div className="libchp_step1_stat-item libchp_step1_difficulty-stats">
                <span className="libchp_step1_stat-label">
                  Difficulty Levels
                </span>
                <div className="libchp_step1_difficulty-bars">
                  {difficultyOptions
                    .filter((option) => option.value !== "n_a")
                    .map((option) => {
                      const stat = stats.difficultyStats[option.value] || {
                        count: 0,
                        percentage: 0,
                      };
                      return (
                        <div
                          key={option.value}
                          className="libchp_step1_difficulty-bar-item"
                        >
                          <div className="libchp_step1_difficulty-label">
                            <div className="libchp_step1_difficulty-icon-wrap">
                              {React.createElement(option.icon, {
                                style: { color: option.color },
                              })}
                            </div>
                            <span className="libchp_step1_difficulty-name">
                              {option.label}
                            </span>
                            <span className="libchp_step1_difficulty-count">
                              {stat.count}/{stats.totalTopics}
                            </span>
                          </div>
                          <div className="libchp_step1_difficulty-bar">
                            <div
                              className="libchp_step1_difficulty-fill"
                              style={{
                                width: `${stat.percentage}%`,
                                backgroundColor: option.color,
                              }}
                            />
                            <span className="libchp_step1_difficulty-percentage">
                              {stat.percentage}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Activity Chart */}
            <div className="libchp_step1_chart-container">
              <div className="libchp_step1_chart-header">
                <h3>Activity Overview</h3>
                <div className="libchp_step1_chart-controls">
                  <button
                    className={`libchp_step1_chart-range-btn ${
                      chartTimeRange === "week" ? "active" : ""
                    }`}
                    onClick={() => setChartTimeRange("week")}
                  >
                    7 Days
                  </button>
                  <button
                    className={`libchp_step1_chart-range-btn ${
                      chartTimeRange === "month" ? "active" : ""
                    }`}
                    onClick={() => setChartTimeRange("month")}
                  >
                    Last Month
                  </button>
                  <button
                    className={`libchp_step1_chart-range-btn ${
                      chartTimeRange === "year" ? "active" : ""
                    }`}
                    onClick={() => setChartTimeRange("year")}
                  >
                    Last Year
                  </button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={stats.chartData[chartTimeRange]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      if (chartTimeRange === "week") {
                        return d.toLocaleDateString(undefined, {
                          weekday: "short",
                        });
                      } else if (chartTimeRange === "month") {
                        return d.getDate(); // # Just show the day number for cleaner display
                      } else {
                        return d.toLocaleDateString(undefined, {
                          month: "short",
                        });
                      }
                    }}
                    interval={
                      chartTimeRange === "year"
                        ? 1
                        : chartTimeRange === "month"
                          ? 2
                          : 1
                    }
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(date) =>
                      new Date(date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="reviews"
                    stroke="#4a90e2"
                    name="Reviews"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="completions"
                    stroke="#4caf50"
                    name="Completions"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Show message if no topics */}
        {topics.length === 0 && <NoTopicsMessage />}
      </main>

      {/* Review History Modal */}
      {showHistoryModal && currentTopicHistory && (
        <div className="libchp_step1_modal-overlay" onClick={closeHistoryModal}>
          <div
            className="libchp_step1_history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="libchp_step1_history-modal-header">
              <h3>Review History</h3>
              <button
                className="libchp_step1_history-modal-close"
                onClick={closeHistoryModal}
              >
                <FaTimes />
              </button>
            </div>
            <div className="libchp_step1_history-modal-content">
              <h4>{currentTopicHistory.name}</h4>
              <div className="libchp_step1_history-summary">
                <p>
                  Total Reviews:{" "}
                  <span className="libchp_step1_history-highlight">
                    {getReviewCount(currentTopicHistory.reviewTimestamps)}
                  </span>
                </p>
                {currentTopicHistory.reviewTimestamps &&
                  currentTopicHistory.reviewTimestamps.length > 0 && (
                    <p>
                      Last Reviewed:{" "}
                      <span className="libchp_step1_history-highlight">
                        {new Date(
                          getLastReviewTimestamp(
                            currentTopicHistory.reviewTimestamps,
                          ),
                        ).toLocaleString()}
                      </span>
                    </p>
                  )}
              </div>
              {currentTopicHistory.reviewTimestamps &&
              currentTopicHistory.reviewTimestamps.length > 0 ? (
                <div className="libchp_step1_review-dates">
                  <h5>Review Sessions</h5>
                  <div className="libchp_step1_review-date-list">
                    {/* # Map through all review timestamps in reverse chronological order */}
                    {[...currentTopicHistory.reviewTimestamps]
                      .sort((a, b) => new Date(b) - new Date(a))
                      .map((timestamp, index) => (
                        <div
                          key={index}
                          className="libchp_step1_review-date-item"
                        >
                          <div className="libchp_step1_review-date-count">
                            {index + 1}
                          </div>
                          <div className="libchp_step1_review-date-time">
                            {new Date(timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <p className="libchp_step1_no-reviews">
                  No review sessions recorded yet.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Start Confirmation Modal */}
      {showStartModal && selectedTopic && (
        <div
          className="libchp_step1_modal-overlay"
          onClick={() => setShowStartModal(false)}
        >
          <div
            className="libchp_step1_start-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="libchp_step1_start-modal-header">
              <h3>Start Topic</h3>
            </div>
            <div className="libchp_step1_start-modal-content">
              <div className="libchp_step1_start-modal-description">
                <p>
                  Would you like to start studying "{selectedTopic.name}" or
                  just view its content?
                </p>
                <p>
                  Starting will track your progress and help you maintain a
                  study schedule.
                </p>
              </div>
              <div className="libchp_step1_start-modal-buttons">
                <button
                  className="libchp_step1_start-button"
                  onClick={handleStartConfirm}
                >
                  Start Studying
                </button>
                <button
                  className="libchp_step1_view-button"
                  onClick={handleViewOnly}
                >
                  View Only
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Step1Chapter;
